import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dispatch, SetStateAction, useCallback, useContext, useState } from "react";

import { CityContext } from "@/contexts/city-context-provider";
import { Label } from "@/components/ui/label";
import { MapToolBoxEntry } from "./map-tool-box-entry";

export type MapToolBoxProps<T> = {
  appMode: T;
  setAppMode: Dispatch<SetStateAction<T>>;
  disabled?: boolean;
  toolboxIcons: ToolboxItem<T>[];
};

export type ToolboxItem<T> = {
  name: string;
  groups: {
    text: string;
    mode: T;
    icon: React.ReactNode;
  }[];
};

export const MapToolBox = <T,>({ appMode: currentAppMode, setAppMode, disabled, toolboxIcons }: MapToolBoxProps<T>) => {
  const { currentCity } = useContext(CityContext);
  const [openWarning, setOpenWarning] = useState<boolean>(false);
  const selectMode = useCallback(
    (selectedMode: T) => {
      if (!currentCity) {
        setOpenWarning(true);
        return;
      }
      setAppMode(selectedMode);
    },
    [currentCity, setAppMode],
  );
  const handleCloseConfirm = useCallback(() => {
    setOpenWarning(false);
  }, []);

  return (
    <div
      className="origin-top-right p-1 rounded-md z-10"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="options-menu"
    >
      {toolboxIcons.map((item) => (
        <div key={item.name} className="mb-2 last:mb-0">
          <Label className="text-xs font-semibold font-sans">{item.name}</Label>
          <div>
            {item.groups.map((icon) => (
              <MapToolBoxEntry
                key={`maptoolbox-${icon.mode}`}
                text={icon.text}
                mode={icon.mode}
                icon={icon.icon}
                onSelect={selectMode}
                selected={currentAppMode === icon.mode}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}
      <AlertDialog open={openWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warning</AlertDialogTitle>
            <AlertDialogDescription>Please select a city</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleCloseConfirm}>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
